import { Component, DestroyRef, ElementRef, OnInit, Renderer2, signal } from '@angular/core';
import { BehaviorSubject, catchError, map, of, ReplaySubject, Subscription, tap } from 'rxjs';
import { ITableColumn } from 'src/app/models/tableColumns';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { ITablePageInfo, jsonHalPageInfoToTablePageInfo } from 'src/app/models/componentInputs/tablePageInfo.interface';
import { formatFromDate } from 'src/app/common/datetime_utility';
import { getAiServiceLogoPath } from 'src/app/common/aiServiceUtils';
import { SidePanelService } from '../../../features/side-panel/services/side-panel.service';
import { aiDataInsightsTableColumns } from 'src/app/models/tableColumns/aiDataInsightsTableColumn.model';
import { AiDataInsightsTableRowViewModel } from 'src/app/models/viewModels/aiDataInsightsTableRowViewModel';
import { JsonHalCollection, JsonHalCollectionPageInfo, JsonHalLinks } from 'src/app/models/api/jsonhal.model';
import { HttpErrorResponse } from '@angular/common/http';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { AiFindingsHttpService } from 'src/app/services/httpServices/detection/aiFindingsHttpService/ai-findings-http.service';
import { FindingModel } from 'src/app/models/api/finding.model';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { IPaginationNavigationModel } from 'src/app/models/paginationNavigation.model';
import { Stack } from 'src/app/common/dataStructures/stack';
import { AgentStepModel, AiService, M365CopilotSource, StepType } from 'src/app/models/api/agentStep.model';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { PaginationOperationEnum } from 'src/app/components/table/table.component';
import { ActiveFiltersHeightService } from 'src/app/services/infra/activeFiltersHeight/active-filters-height.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MenuAction } from 'src/app/models/menuAction.model';
import { RowActionClicked } from 'src/app/components/table-row/table-row.component';
import { MatDialog } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { AiDataInsightsFiltersModel } from 'src/app/models/filters/implementations/pageFilters/aiDataInsightsFilters.model';
import { ISelectOption } from 'src/app/models/selectOptions';
import { PageWithFiltersEnum } from 'src/app/models/filters/pageWithFilters.enum';
import { FiltersService } from 'src/app/services/filters/filters.service';

interface fileStatsData {
  firstDetection: FindingModel;
  latestDetection: FindingModel;
  numOfFindings: number;
}
type fileStats = Record<string, fileStatsData>;
@Component({
  selector: 'app-enterprise-ai-data-insights',
  templateUrl: './enterprise-ai-data-insights.component.html',
  styleUrls: ['./enterprise-ai-data-insights.component.scss'],
})
export class EnterpriseAiDataInsightsComponent implements OnInit {
  public columns = signal<ITableColumn[]>(aiDataInsightsTableColumns);
  public isLoading = signal<boolean>(true);
  public showNoData = false;
  public totalItems = 0;
  public pageInfo$: BehaviorSubject<ITablePageInfo>;
  public checkedDataInsightIds: Set<string> = new Set();
  public sortedColumn$ = new BehaviorSubject<SortColumn | undefined>(undefined);
  public expandFilterResultControl = new FormControl(false);
  private _currentDataInsightsById: { [dataInsightId: string]: AiDataInsightsTableRowViewModel } = {};

  public tableColumns = aiDataInsightsTableColumns;
  public pageFilters: AiDataInsightsFiltersModel;

  public currentDataInsightsViewModel: BehaviorSubject<BehaviorSubject<AiDataInsightsTableRowViewModel>[]>;
  private readonly _sortOnChangeSubscription$: Subscription;
  private _firstPageSubscription$: Subscription;
  public dataInsightsFirstPage$: BehaviorSubject<AiDataInsightsTableRowViewModel[]> = new BehaviorSubject<
    AiDataInsightsTableRowViewModel[]
  >([]);
  public dataInsightsCurrentPage$: BehaviorSubject<AiDataInsightsTableRowViewModel[]> = new BehaviorSubject<
    AiDataInsightsTableRowViewModel[]
  >([]);
  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private _fetchByPageSubscription$: Subscription;
  private _paginationNavigation: IPaginationNavigationModel;
  private _filtersOnChangeSubscription$: Subscription;
  public readonly defaultPageSize: number;
  public isFiltersMenuOpen = false;
  public multiSelectedMenuActions$: BehaviorSubject<MenuAction[]> = new BehaviorSubject<MenuAction[]>([]);

  constructor(
    private readonly _sidePanel: SidePanelService,
    private readonly _aiFindingsHttpService: AiFindingsHttpService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _logger: LoggerService,
    private readonly _activeFiltersHeightService: ActiveFiltersHeightService,
    private readonly _renderer: Renderer2,
    private readonly _el: ElementRef,
    private readonly _destroyRef: DestroyRef,
    public matDialog: MatDialog,
    private readonly _router: Router,
    private readonly _notificationService: NotificationsService,
    private readonly _filtersService: FiltersService
  ) {
    this.defaultPageSize = 500;
    this._initializePageInfo();
  }

  ngOnInit(): void {
    this.pageFilters = this._filtersService.getFilters(
      PageWithFiltersEnum.AiDataInsights
    ) as AiDataInsightsFiltersModel;
    this._subscribeToHandleFiltersOnChange();
    this._activeFiltersHeightService.height$
      .pipe(
        tap((height: number) =>
          this._renderer.setStyle(this._el.nativeElement, '--applied-filters-height', `${height}px`, 1)
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  private _transformToViewModel(items: FindingModel[], row: FindingModel): AiDataInsightsTableRowViewModel[] {
    const results: AiDataInsightsTableRowViewModel[] = [];
    if (!(row.evidence && row.evidence.length > 0)) {
      return results;
    }
    const evidenceFiles = row.evidence.map((evidence) => evidence.additionalData['sensitiveFileNames']);
    for (const step of row.relatedSteps) {
      if (step.type === StepType.ragStep && step.service === AiService.m365Copilot) {
        for (const citedSource of step.citedSources) {
          if (citedSource.name && evidenceFiles.includes(citedSource.name)) {
            const fileStats = this.computeFileStats(items, citedSource.id);
            results.push(
              this._transformToViewModelSingleRow(
                step,
                citedSource,
                fileStats[citedSource.id].latestDetection,
                fileStats[citedSource.id].firstDetection,
                fileStats[citedSource.id].numOfFindings
              )
            );
          }
        }
      }
    }
    return results;
  }

  private _transformToViewModelSingleRow(
    step: AgentStepModel,
    row: M365CopilotSource,
    latestFinding: FindingModel,
    firstFinding: FindingModel,
    numOfFindings: number
  ): AiDataInsightsTableRowViewModel {
    const queryParamsFirstFinding = {
      tab: 'findings',
      integration: firstFinding.integration.id,
      finding: firstFinding.id,
    };
    const firstFindingUrl = this._router.createUrlTree([], { queryParams: queryParamsFirstFinding }).toString();
    const queryParamsLastFinding = {
      tab: 'findings',
      integration: latestFinding.integration.id,
      finding: latestFinding.id,
    };
    const lastFindingUrl = this._router.createUrlTree([], { queryParams: queryParamsLastFinding }).toString();
    const menuActions: MenuAction[] = [];
    menuActions.push({
      name: 'exclude',
      displayNameLabel: 'AiDataInsightsActions.exclude',
      disabled: false,
    } as MenuAction);
    return {
      rowId: row.id,
      id: row.id,
      name: row.name ? row.name : '',
      integrationId: step.integration.id,
      firstDetectionDate: formatFromDate(firstFinding.timestamp),
      firstDetectionUrl: firstFindingUrl,
      lastDetectionUrl: lastFindingUrl,
      lastDetectionDate: formatFromDate(latestFinding.timestamp),
      numberOfFindings: numOfFindings,
      clickable: false,
      evidence: 'File is sensitive due to its identifiable naming',
      platformIcon: getAiServiceLogoPath(step.service),
      menuActions: menuActions,
    };
  }

  private _initializePageInfo() {
    this.pageInfo$ = new BehaviorSubject({
      number: 1,
      currSize: 1,
      maxSize: 2,
      totalItems: 1,
      totalPages: 1,
    });
  }

  public onCheckboxClick(event: Set<string>): void {
    this.checkedDataInsightIds = event;
    this._updateSupportedActionsForSelectedRows();
  }

  public columnsSelected(selectedColumns: ITableColumn[]) {
    this.columns.set(structuredClone(selectedColumns));
  }

  private _updateSupportedActionsForSelectedRows(): void {
    const menuActions: MenuAction[] = [];
    menuActions.push({
      name: 'exclude',
      displayNameLabel: 'AiDataInsightsActions.exclude',
      disabled: false,
    } as MenuAction);

    this.multiSelectedMenuActions$.next(menuActions);
  }

  private computeFileStats(items: FindingModel[], fileId: string): fileStats {
    const fileStats: fileStats = {};
    items.forEach((item) => {
      let alreadyAddedFileToCurrentFinding = false;
      item.relatedSteps.forEach((step) => {
        if (step.type !== StepType.ragStep || step.service !== AiService.m365Copilot) {
          return;
        }
        const citedSource = step.citedSources.find((source) => source.id === fileId);
        if (!citedSource) {
          return;
        }
        if (!fileStats[fileId]) {
          fileStats[fileId] = {
            firstDetection: item,
            latestDetection: item,
            numOfFindings: 1,
          };
          alreadyAddedFileToCurrentFinding = true;
        } else if (!alreadyAddedFileToCurrentFinding) {
          fileStats[fileId].firstDetection = item;
          fileStats[fileId].numOfFindings += 1;
          alreadyAddedFileToCurrentFinding = true;
        }
      });
    });

    return fileStats;
  }

  public actionOnClick(actionClicked: RowActionClicked): void {
    const success = new ReplaySubject<boolean>(1);
    const dataInsightRow = actionClicked.row.getValue() as AiDataInsightsTableRowViewModel;
    this._aiFindingsHttpService
      .removeFileFromRag(dataInsightRow.integrationId, dataInsightRow.id)
      .pipe(
        map(() => {
          this._notificationService.success(`File: ${dataInsightRow.name} was excluded successfully`);
          return true;
        }),
        catchError((error: HttpErrorResponse) => {
          this._notificationService.error(`File: ${dataInsightRow.name} could not be excluded. ${error.error.message}`);
          return of(false);
        })
      )
      .subscribe(success);
  }

  private _fetchFirstPage(filterOptions: ISelectedFilters, sortData?: SortColumn) {
    this._cancelOngoingHttpRequest();
    this._tableBehaviorChangeBeforeFetch();

    this._firstPageSubscription$ = this._aiFindingsHttpService
      .startGetFindings(filterOptions, sortData, this.defaultPageSize)
      .subscribe({
        next: (response) => this._handleDataInsightsCollectionResponse(response),
        error: (err) => this._fetchRequestOnError(err),
      });
  }

  private async _handleDataInsightsCollectionResponse(response: JsonHalCollection<FindingModel>) {
    this._handlePaginationUrls(response._links);
    let realTotalItems = 0;
    const items = response._embedded.items;

    if (!items || items.length == 0) {
      this.showNoData = true;
      this.dataInsightsCurrentPage$.next([]);
    } else {
      this.showNoData = false;
      const dataInsightsViewModelTemp: AiDataInsightsTableRowViewModel[] = [];
      this._currentDataInsightsById = {};
      items.forEach((item) => dataInsightsViewModelTemp.push(...this._transformToViewModel(items, item)));
      const dataInsightsViewModelTempUnique = dataInsightsViewModelTemp.filter(
        (item, index, array) => array.findIndex((i) => i.id === item.id) === index
      );
      for (const item of dataInsightsViewModelTempUnique) {
        this._currentDataInsightsById[item.id] = item;
      }
      realTotalItems = dataInsightsViewModelTempUnique.length;
      this.dataInsightsCurrentPage$.next(dataInsightsViewModelTempUnique);
    }
    this._handlePageInfo(response.page, realTotalItems);
    this.isLoading$.next(false);
  }

  private _cancelOngoingHttpRequest() {
    if (this._firstPageSubscription$) {
      this._firstPageSubscription$.unsubscribe();
    }

    if (this._fetchByPageSubscription$) {
      this._fetchByPageSubscription$.unsubscribe();
    }
  }

  private _fetchRequestOnError(error: HttpErrorResponse) {
    this.dataInsightsCurrentPage$.next([]);
    this.isLoading$.next(false);
    this.showNoData = true;
    this._httpErrorHandlerService.handle(error);
  }

  private _tableBehaviorChangeBeforeFetch() {
    this.dataInsightsCurrentPage$.next([]);
    this.isLoading$.next(true);
  }

  private _handlePaginationUrls(links: JsonHalLinks) {
    if (!this._paginationNavigation) {
      this._paginationNavigation = {
        currentPage: links.self.href,
        nextPage: links.next?.href,
        previousPage: new Stack<string>(),
      } as IPaginationNavigationModel;
    } else {
      this._paginationNavigation.currentPage = links.self.href;
      this._paginationNavigation.nextPage = links.next?.href;
    }
  }

  private _handlePageInfo(pageInfo: JsonHalCollectionPageInfo, realTotalItems: number) {
    this.totalItems = realTotalItems;
    pageInfo.totalItems = realTotalItems;
    // pageInfo.totalItems = realTotalItems;
    const tablePageInfo = jsonHalPageInfoToTablePageInfo(pageInfo, this.defaultPageSize);

    if (!this.pageInfo$) {
      this.pageInfo$ = new BehaviorSubject(tablePageInfo);
    } else {
      this.pageInfo$.next(tablePageInfo);
    }
  }

  private _fetchByPage(continuationToken: string) {
    this._cancelOngoingHttpRequest();
    this._tableBehaviorChangeBeforeFetch();

    this._fetchByPageSubscription$ = this._aiFindingsHttpService
      .getFindingsByContinuationToken(continuationToken)
      .subscribe(
        (response) => this._handleDataInsightsCollectionResponse(response),
        (error) => this._fetchRequestOnError(error)
      );
  }

  private _doPaginationWhenTokenExist(url: string) {
    const continuationToken = this._tokenExtractor(url);

    if (continuationToken) {
      this._fetchByPage(continuationToken);
    }
  }

  private _subscribeToHandleFiltersOnChange() {
    this._filtersOnChangeSubscription$ = this.pageFilters.onUpdate$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((selectedFilters) => {
        this._fetchFirstPage(selectedFilters);
      });
  }

  private _tokenExtractor(url: string): string | null {
    const continuationToken = new URLSearchParams(url.slice(url.indexOf('?'))).get('continuationToken');
    if (!continuationToken) {
      this._logger.error('Failed to extract continuation token from json hal next activity resource page link');
      return null;
    }

    return continuationToken;
  }

  public onPagination(operation: PaginationOperationEnum) {
    if (!this.isLoading$.value) {
      if (operation == PaginationOperationEnum.next) {
        this._paginationNavigation.previousPage.push(this._paginationNavigation.currentPage);
      }

      const pageUrl =
        operation == PaginationOperationEnum.next
          ? this._paginationNavigation.nextPage
          : this._paginationNavigation.previousPage.pop();
      if (pageUrl) {
        this._doPaginationWhenTokenExist(pageUrl);
      }
    }
  }

  public tablesFiltersChange(selectedOptions: ISelectOption[]) {
    this.pageFilters.updateFilterFromUserSelection(undefined, selectedOptions);
  }

  public filtersOnMenuStateChange(menuState: boolean) {
    this.isFiltersMenuOpen = menuState;
  }
}
